import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import HTMLRenderer from 'react-html-renderer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Image from 'components/Common/Image'
import useToggle from 'hooks/useToggle'
import Modal from 'components/Luxkit/Modal/Modal'
import TextButton from 'components/Luxkit/Button/TextButton'
import Accordion from 'components/Luxkit/Accordion'

const Root = styled.div`
  display: grid;
  padding: 0 ${rem(14)};
  grid-template-areas:
    "top-bar"
    "."
    "overview"
    "."
    "title"
    "."
    "details"
    "."
    "description"
    "facilities";
  grid-template-columns: 1fr;
  grid-template-rows:
    auto
    ${rem(8)}
    auto
    ${rem(32)}
    auto
    auto
    auto
    ${rem(16)}
    auto;
  max-width: 100%;

  ${mediaQueryUp.sm} {
    padding: 0;
    grid-template-areas:
      "top-bar top-bar top-bar"
      ". . ."
      "journey . overview"
      ". . overview"
      "title . overview"
      ". . overview"
      "details . overview"
      ". . overview"
      "description . overview"
      "facilities . overview";
    grid-template-columns: 1fr ${rem(32)} ${rem(448)};
    grid-template-rows:
      auto
      ${rem(8)}
      auto
      ${rem(4)}
      auto
      auto
      auto
      ${rem(24)}
      auto
      auto;
  }
`

const TitleArea = styled.div`
  grid-area: title;
`

const OverviewArea = styled.div`
  grid-area: overview;
  align-self: center;
`

const FacilitiesArea = styled.div`
  grid-area: facilities;
`

const DescriptionArea = styled.div`
  grid-area: description;
`

const ShipImage = styled(Image)`
  max-width: 100%;
  max-height: 100%;
  border-radius: ${props => props.theme.borderRadius.medium};
`
const ShipImageContainer = styled.div`
  display: flex;

  ${mediaQueryUp.md} {
    display: block;
    flex-shrink: 0;
  }
`

const FacilitiesText = styled(BodyText)`
  padding-top: ${rem(12)};

  &.list {
    padding-top: ${rem(4)};
  }
`

const ShipFactsRenderer = styled(HTMLRenderer)`
  > div {
    display: flex;
  }
`

const FactText = styled(BodyText)`
  display: flex;
  padding-top: ${rem(4)};
  gap: ${rem(4)};

  &:last-child {
    padding-bottom: ${rem(20)};
  }
`

interface Props { offer: App.CruiseOffer }

function AboutTheShip(props: Props) {
  const {
    offer,
  } = props
  const [isModalOpen, setIsModalOpen] = useToggle(false)

  const { ship } = offer

  const operatorName = offer.cruiseLine.name

  const shipFacts = useMemo(() => {
    const facts = ship.shipInfo?.find(info => info.dataTypeName === 'Facts')
    if (facts && facts.description) {
      return facts.description
    }
    return ''
  }, [ship.shipInfo])

  const shipFacilities = useMemo(() => {
    const facilities = ship.shipInfo?.filter(info => info.dataTypeName === 'General')

    const sanitizedFacilities = facilities.map(category => {
      const sanitizedDescription = category.description.replaceAll(/<\/?br\/?>/g, '')
      return { title: category.title, description: sanitizedDescription }
    })

    return sanitizedFacilities
  }, [ship.shipInfo])

  const shipImage = ship.images.length > 0 ? ship.images[0] : null

  return (
    <Root>
      <TitleArea>
        <Heading as="h4" variant="heading3">
          {ship.name}
        </Heading>
        <div className="subtitle">
          <BodyText variant="medium"><span>{operatorName}</span></BodyText>
        </div>
      </TitleArea>

      <DescriptionArea>
        <ShipFactsRenderer
            html={shipFacts}
            components={{
              ul: ({ children }) => <BodyText variant="medium" weight="normal">{children}</BodyText>,
              li: ({ children }) => <FactText variant="medium" weight="bold">{children}</FactText>,
              b: ({ children }) => <BodyText variant="medium" weight="normal">{children}</BodyText>,
            }}
          />
      </DescriptionArea>

      <FacilitiesArea>
        {shipFacilities.length > 0 &&
          <TextButton
            size="large"
            kind="secondary"
            onClick={setIsModalOpen}
          >
            View Facilities
          </TextButton>
        }
      </FacilitiesArea>

      <OverviewArea>
        {shipImage && <ShipImageContainer>
          <ShipImage
            id={shipImage.id}
            quality="best"
            alt={ship.name}
          />
        </ShipImageContainer>}
      </OverviewArea>

      <Modal isOpen={isModalOpen} onClose={setIsModalOpen} title="Facilities">
        {shipFacilities.map(facility => (
          <Accordion size="small" heading={facility.title} key={facility.title}>
            <HTMLRenderer
              html={facility.description}
              components={{
                h4: ({ children }) => <Heading variant="heading4">{children}</Heading>,
                b: ({ children }) => <FacilitiesText variant="large" weight="bold">{children}</FacilitiesText>,
                p: ({ children }) => <BodyText variant="medium">{children}</BodyText>,
                ul: ({ children }) => <BodyText variant="small">{children}</BodyText>,
                li: ({ children }) => <FacilitiesText variant="medium" className="list">{children}</FacilitiesText>,
              }}/>
          </Accordion>
        ))}
      </Modal>
    </Root>
  )
}

export default AboutTheShip
